import { useAuth0 } from "@auth0/auth0-react";
import checkIfAuthenticatedAndAuthenticate from "Logic/authentication";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchOrganizations } from "feature/admin/actions";
import { useEffect } from "react";
import { Disclosure } from "@headlessui/react";
import { faFolder, faUserCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { resetSelectedUsers, selectOrganizations } from "feature/admin/slice";
import { UsersTab } from "feature/admin/types";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const UserListType: UsersTab[] = [
  {
    title: "All Users",
    path: "all",
  },
  {
    title: "Unassigned Users",
    path: "unassigned",
  },
  {
    title: "Pending Users",
    path: "pending",
  },
];

function Organizations() {
  const organizations = useAppSelector(selectOrganizations);
  const { orgId } = useParams();

  return (
    <ul role="list" className="-mx-2 space-y-1">
      <Disclosure as="div">
        {({ open }) => (
          <>
            <Disclosure.Button className="flex w-full items-center gap-x-3 rounded-md bg-gray-800 p-2 text-left text-sm font-semibold leading-6 text-white hover:bg-gray-800">
              <FontAwesomeIcon
                icon={faFolder}
                className="h-6 w-6 shrink-0 text-white"
                aria-hidden="true"
              />
              Organizations
              <FontAwesomeIcon
                icon={faChevronRight}
                className={classNames(
                  open ? "rotate-90 text-white" : "text-white",
                  "ml-auto h-5 w-5 shrink-0"
                )}
                aria-hidden="true"
              />
            </Disclosure.Button>
            <Disclosure.Panel as="ul" className="mt-1 px-2">
              {organizations.map((organization) => (
                <li key={organization.organisation_id}>
                  <NavLink
                    to={`organization/${organization.organisation_id}`}
                    className={classNames(
                      organization.organisation_id === orgId
                        ? "bg-gray-800"
                        : "hover:bg-gray-800",
                      "block rounded-md py-2 pr-2 pl-9 text-sm leading-6 text-white"
                    )}
                  >
                    {organization.name}
                  </NavLink>
                </li>
              ))}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </ul>
  );
}

function Users() {
  const dispatch = useAppDispatch();
  const onTabChange = () => {
    dispatch(resetSelectedUsers());
  };
  return (
    <ul role="list" className="-mx-2 space-y-1">
      <Disclosure as="div">
        {({ open }) => (
          <>
            <Disclosure.Button className="flex w-full items-center gap-x-3 rounded-md bg-gray-800 p-2 text-left text-sm font-semibold leading-6 text-white hover:bg-gray-800">
              <FontAwesomeIcon
                icon={faUserCircle}
                className="h-6 w-6 shrink-0 text-white"
                aria-hidden="true"
              />
              Users
              <FontAwesomeIcon
                icon={faChevronRight}
                className={classNames(
                  open ? "rotate-90 text-white" : "text-white",
                  "ml-auto h-5 w-5 shrink-0"
                )}
                aria-hidden="true"
              />
            </Disclosure.Button>
            <Disclosure.Panel as="ul" className="mt-1 px-2">
              {UserListType.map((user, index) => (
                <li key={index} onClick={onTabChange}>
                  <NavLink
                    to={`users/${user.path}`}
                    className={({ isActive }) =>
                      classNames(
                        isActive ? "bg-gray-800" : "hover:bg-gray-800",
                        "my-2 block rounded-md py-2 pr-2 pl-9 text-sm leading-6 text-white"
                      )
                    }
                  >
                    {user.title}
                  </NavLink>
                </li>
              ))}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </ul>
  );
}
function Layout() {
  return (
    <div>
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6">
          <div className="flex h-16 shrink-0 items-center text-white">
            <p>Admin</p>
          </div>
          <nav className="flex flex-col gap-y-5">
            <ul role="list" className="flex flex-col gap-y-7">
              <li>
                <Organizations />
              </li>
            </ul>
            <ul role="list" className="flex flex-col gap-y-7">
              <li>
                <Users />
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <main className="py-10 lg:pl-72">
        <div className="px-4 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Admin() {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(fetchOrganizations());
  }, []);

  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    checkIfAuthenticatedAndAuthenticate(
      loginWithRedirect,
      isAuthenticated,
      isLoading
    );
  }, [dispatch, isAuthenticated, isLoading, loginWithRedirect]);

  return <Layout />;
}

export default Admin;
